import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Size {
  size: string;
  stock: number;
}

interface SizeManagementProps {
  sizes: Size[];
  onChange: (sizes: Size[]) => void;
}

export const SizeManagement = ({ sizes, onChange }: SizeManagementProps) => {
  const [newSize, setNewSize] = useState<Size>({ size: '', stock: 0 });

  const addSize = () => {
    if (!newSize.size || newSize.stock < 0) return;
    
    // Vérifier si la taille existe déjà
    if (sizes.some(s => s.size.toUpperCase() === newSize.size.toUpperCase())) {
      alert('Cette taille existe déjà');
      return;
    }
    
    onChange([...sizes, { ...newSize, size: newSize.size.toUpperCase() }]);
    setNewSize({ size: '', stock: 0 });
  };

  const removeSize = (index: number) => {
    onChange(sizes.filter((_, i) => i !== index));
  };

  const predefinedSizes = [
    { label: 'XS', value: 'XS' },
    { label: 'S', value: 'S' },
    { label: 'M', value: 'M' },
    { label: 'L', value: 'L' },
    { label: 'XL', value: 'XL' },
    { label: 'XXL', value: 'XXL' },
    { label: '3XL', value: '3XL' },
    { label: '4XL', value: '4XL' },
  ];

  const shoeSizes = Array.from({ length: 20 }, (_, i) => ({
    label: `${35 + i}`,
    value: `${35 + i}`
  }));

  return (
    <div className="space-y-4">
      <Label>Tailles disponibles</Label>
      
      {/* Liste des tailles ajoutées */}
      <div className="flex flex-wrap gap-2">
        {sizes.map((sizeItem, index) => (
          <Badge 
            key={index} 
            variant="outline" 
            className="px-3 py-2 flex items-center gap-2 text-base"
          >
            <span className="font-semibold">{sizeItem.size}</span>
            <span className="text-xs text-muted-foreground">({sizeItem.stock} unités)</span>
            <button
              type="button"
              onClick={() => removeSize(index)}
              className="ml-1 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {sizes.length === 0 && (
          <p className="text-sm text-muted-foreground">Aucune taille ajoutée</p>
        )}
      </div>

      {/* Formulaire d'ajout */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="sizeName" className="text-xs">Taille</Label>
            <Input
              id="sizeName"
              placeholder="Ex: M, 42, L"
              value={newSize.size}
              onChange={(e) => setNewSize({ ...newSize, size: e.target.value.toUpperCase() })}
              maxLength={10}
            />
          </div>
          <div>
            <Label htmlFor="sizeStock" className="text-xs">Stock</Label>
            <Input
              id="sizeStock"
              type="number"
              min="0"
              value={newSize.stock}
              onChange={(e) => setNewSize({ ...newSize, stock: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        
        {/* Tailles vêtements prédéfinies */}
        <div className="space-y-2">
          <Label className="text-xs">Tailles vêtements :</Label>
          <div className="flex flex-wrap gap-2">
            {predefinedSizes.map((size) => (
              <button
                key={size.value}
                type="button"
                onClick={() => setNewSize({ ...newSize, size: size.value })}
                className="px-3 py-1 rounded border hover:bg-accent text-sm font-medium"
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tailles chaussures */}
        <div className="space-y-2">
          <Label className="text-xs">Pointures chaussures (35-54) :</Label>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {shoeSizes.map((size) => (
              <button
                key={size.value}
                type="button"
                onClick={() => setNewSize({ ...newSize, size: size.value })}
                className="px-2 py-1 rounded border hover:bg-accent text-xs"
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>

        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addSize}
          disabled={!newSize.size}
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter cette taille
        </Button>
      </div>
    </div>
  );
};
